import { XMarkIcon } from '@heroicons/react/16/solid';
import { classSet, Icon } from '@platform/ui-library';
import {
  FC,
  MouseEvent,
  useEffect,
  useRef
} from 'react';
import { default as ReactDOM } from 'react-dom';
import { rootModuleId } from '@app/const';
import { Carousel } from '@app/form_components';
import { useAsync, useCanvasTabs } from '@app/hooks';
import styles from './CanvasTabStrip.module.scss';

/** Scroll only the carousel viewport so `tab` is fully visible horizontally. */
function scrollTabIntoCarouselViewport(tab: HTMLElement): void {
  const viewport = tab.closest('[data-part="viewport"]');

  if (!(viewport instanceof HTMLElement)) {
    return;
  }

  const tabRect = tab.getBoundingClientRect();
  const viewportRect = viewport.getBoundingClientRect();

  if (tabRect.left < viewportRect.left) {
    viewport.scrollTo({
      left: viewport.scrollLeft - (viewportRect.left - tabRect.left),
      behavior: 'smooth'
    });
    return;
  }

  if (tabRect.right > viewportRect.right) {
    viewport.scrollTo({
      left: viewport.scrollLeft + (tabRect.right - viewportRect.right),
      behavior: 'smooth'
    });
  }
}

export const CanvasTabStrip: FC = () => {
  const [ContextMenuWidget] = useAsync(async () => {
    return (await import('@app/widgets/ContextMenuWidget')).ContextMenuWidget;
  }, []);
  const activeTabId = useCanvasTabs((s) => s.activeTabId);
  const tabs = useCanvasTabs((s) => s.tabs);
  const { closeTab, moveTab, setActiveTab } = useCanvasTabs.getState();
  const tabListRef = useRef<HTMLDivElement>(null);

  const handleContextMenuOpen = (
    tabId: string,
    event: MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    const contextContainer = $('.context_menu');
    const container = contextContainer.get(0);
    if (
      tabId === rootModuleId
      || !ContextMenuWidget
      || !container
    ) {
      return;
    }

    ReactDOM.render((
      <ContextMenuWidget
        domContext={contextContainer}
        pos={{ top: event.clientY, left: event.clientX }}
        tabId={tabId}
        type="canvastab"
      />
    ), container);
  };

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      const activeTab = tabListRef.current?.querySelector(
        `[data-canvas-tab-id="${CSS.escape(activeTabId)}"]`
      );

      if (activeTab instanceof HTMLElement) {
        scrollTabIntoCarouselViewport(activeTab);
      }
    });

    return () => {
      cancelAnimationFrame(frame);
    };
  }, [activeTabId, tabs]);

  useEffect(() => {
    const tabList = tabListRef.current;
    if (!tabList) {
      return;
    }

    const sortableRow = $(tabList);
    sortableRow.sortable({
      axis: 'x',
      cancel: `[data-canvas-tab-id="${rootModuleId}"]`,
      stop: (_event, ui) => {
        const tabId = ui.item.attr('data-canvas-tab-id')!;
        const toIndex = Array.from(tabList.children).findIndex((element) => {
          return element.getAttribute('data-canvas-tab-id') === tabId;
        });

        // Cancel jQuery moving the tab, let React do it instead.
        sortableRow.sortable('cancel');
        moveTab(tabId, toIndex);
      }
    });

    return () => {
      sortableRow.sortable('destroy');
    };
  }, [moveTab]);

  useEffect(() => {
    // jQuery UI caches the item list, so tabs added after init need a refresh.
    if (tabListRef.current) {
      $(tabListRef.current).sortable('refresh');
    }
  }, [tabs]);

  return (
    <Carousel
      classNames={{ carousel: styles.tabStrip }}
      remeasureKey={tabs.map((tab) => tab.label).join('\0')}
    >
      <div ref={tabListRef} className={styles.tabList} role="tablist">
        {tabs.map((tab) => {
          return (
            <div
              key={tab.id}
              className={classSet([
                styles.tabItem,
                (tab.id !== activeTabId) ? null : styles.tabActive,
                (!tab.isCommonModule) ? null : styles.tabCommonModule
              ])}
              data-canvas-tab-id={tab.id}
            >
              <button
                type="button"
                className={styles.tabLabel}
                onClick={() => setActiveTab(tab.id)}
                onContextMenu={(event) => handleContextMenuOpen(tab.id, event)}
              >
                {tab.label}
              </button>
              {(tab.id === rootModuleId) ? null : (
                <button
                  aria-label={`Close ${tab.label}`}
                  className={styles.tabClose}
                  onClick={(event) => {
                    event.stopPropagation();
                    window.apm.removeValidationError(tab.id);
                    closeTab(tab.id);
                  }}
                  type="button"
                >
                  <Icon icon={XMarkIcon} />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </Carousel>
  );
};
